import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { MenuOptionValue, NestedPick, Paginated, PaginationQueryWithFields } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { CarTask, CarTaskNoCarDependency, InsertCarTask, UpdateCarTask } from "./types";

export const useCarTaskApi = () => {
  const api = useApi();

  const getAdminCarTask = useCallback(
    (carTaskId: string, signal: AbortSignal) => {
      const url = `/admin/car_tasks/${carTaskId}`;
      return useSWRMutation<CarTask | null, CustomError, string>(url, (url) => api.get<CarTask>({ url, signal }));
    },
    [api],
  );

  const useAdminCarTasks = useCallback(
    <K extends keyof CarTaskNoCarDependency>(
      query: PaginationQueryWithFields<NestedPick<CarTaskNoCarDependency, K>>,
      signal: AbortSignal,
      carId?: string,
      hasPermission?: boolean,
    ) => {
      const url = buildUrl<NestedPick<CarTaskNoCarDependency, K>>(
        carId ? `/admin/cars/${carId}/tasks` : "/admin/car_tasks",
        query,
      );

      const key: string | null = hasPermission === true ? url : null;

      return useSWR<Paginated<NestedPick<CarTaskNoCarDependency, K>> | null, CustomError, string | null>(key, (u) =>
        api.get<Paginated<NestedPick<CarTaskNoCarDependency, K>>>({ url: u!, signal }),
      );
    },
    [api],
  );

  const insertAdminCarTask = useCallback(
    async (carId: string, body: InsertCarTask) => {
      const url = `/admin/cars/${carId}/tasks`;
      const id = await api.post<InsertCarTask, { id: string }>(url, body);
      api.mutateCache([
        url,
        `/admin/car_tasks`,
        `/admin/cars/${carId}`,
        `/admin/cars/${carId}/tasks`,
        `/admin/cars/${carId}/overview`,
        `/admin/car_tasks/status`,
        `/admin/cars/notifications`,
      ]);
      return id;
    },
    [api],
  );

  const updateAdminCarTask = useCallback(
    async (carTask: CarTask, body: UpdateCarTask) => {
      const url = `/admin/car_tasks/${carTask.id}`;
      await api.put<UpdateCarTask>(url, body);
      api.mutateCache([
        url,
        `/admin/car_tasks`,
        `/admin/cars/${carTask.car.id}`,
        `/admin/cars/${carTask.car.id}/tasks`,
        `/admin/cars/${carTask.car.id}/overview`,
        `/admin/car_tasks/status`,
        `/admin/cars/notifications`,
      ]);
      return carTask.id;
    },
    [api],
  );

  const deleteCarTask = useCallback(
    async (id: string, carId: string, forceDelete?: boolean) => {
      const baseUrl = `/admin/car_tasks/${id}`;
      const url = forceDelete ? `${baseUrl}?force=true` : baseUrl;
      await api.del(url).then(() => {
        api.mutateCache([
          `/admin/car_tasks`,
          `/admin/cars/${carId}/tasks`,
          `/admin/car_tasks/status`,
          `/admin/cars/notifications`,
        ]);
      });
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/car_tasks/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  return {
    insertAdminCarTask,
    useAdminCarTasks,
    updateAdminCarTask,
    deleteCarTask,
    getFieldFilters,
    getAdminCarTask,
  };
};
